/**
 * VaultIntelligenceCard · prominent Document Intelligence Vault panel.
 *
 * Meant to be shown near the top of the dossier page so the advisor and
 * client see in one glance:
 *   - how many documents are attached,
 *   - how they distribute by category / evidence status,
 *   - the global reliability score (0..100),
 *   - the latest AI extractions with citations,
 *   - the inconsistencies detected between declared vs extracted data.
 *
 * Also exposes a single "Analyser les documents en attente" action to
 * batch-run Claude Vision on any documented-but-unanalysed piece.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  ShieldCheck, AlertTriangle, Wand2, Loader2, Sparkles,
  FileCheck2, FileWarning, Layers,
} from "lucide-react";


const CATEGORY_FR = {
  formation: "Formation", experience: "Expérience", fiscalite: "Fiscalité",
  identite: "Identité", revenus: "Revenus", actifs: "Actifs",
  passifs: "Passifs", assurances: "Assurances", projet: "Projet",
};

const STATUS_FR = {
  declared: "Déclaré", documented: "Documenté", analyzed: "Analysé IA",
  confirmed: "Confirmé", verified: "Vérifié",
  contradictory: "Contradictoire", expired: "Expiré",
  illegible: "Illisible", incomplete: "Incomplet", na: "N/A",
};


export default function VaultIntelligenceCard({ dossierId, onRefresh }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  const load = useCallback(async () => {
    if (!dossierId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(
        `/vault/summary?dossier_id=${encodeURIComponent(dossierId)}`
      );
      setSummary(data);
    } catch (err) {
      // Non-fatal — some dossiers may not have vault entries yet
      setSummary({ total_documents: 0, reliability_score: 0,
                    by_status: {}, by_category: {}, extractions: [], inconsistencies: [] });
    } finally { setLoading(false); }
  }, [dossierId]);

  useEffect(() => { load(); }, [load]);

  const runBatchAnalyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(
        `/vault/dossier/${dossierId}/analyze-pending`
      );
      const okCount = (data.processed || []).filter((p) => p.ok).length;
      const failCount = (data.processed || []).length - okCount;
      if (data.total_pending === 0) {
        toast.info("Aucun document en attente d'analyse.");
      } else {
        toast.success(
          `${okCount}/${data.total_pending} document${data.total_pending > 1 ? "s" : ""} analysé${okCount > 1 ? "s" : ""}` +
          (failCount ? ` · ${failCount} échec${failCount > 1 ? "s" : ""}` : "")
        );
      }
      await load();
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analyse impossible.");
    } finally { setAnalyzing(false); }
  };

  const rel = summary?.reliability_score ?? 0;
  const relTone = rel >= 70 ? "#059669" : rel >= 40 ? "#B45309" : "#B91C1C";
  const relLabel = rel >= 70 ? "Solide" : rel >= 40 ? "Partielle" : "Faible";

  const documentedPending = summary?.by_status?.documented || 0;
  const analyzedPending   = summary?.analyzed_pending_count || 0;
  const totalDocs         = summary?.total_documents || 0;
  const issues            = summary?.inconsistencies || [];
  const extractions       = summary?.extractions || [];

  return (
    <section className="mt-12" data-testid="vault-intelligence-card">
      <div className="rounded-2xl border border-[#0B2545]/15 bg-gradient-to-br from-[#FBF7EE] via-white to-[#F6EFDE]/50 shadow-[0_1px_4px_rgba(11,37,69,0.05)] overflow-hidden">
        <header className="flex flex-wrap items-start gap-4 px-6 py-5 border-b border-[#E4E1D8]">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-[#7A5B14]">
              Document Intelligence Vault
            </div>
            <h2 className="mt-1 font-display text-2xl md:text-3xl tracking-tight leading-tight text-[#0B2545]">
              Ce qui est <em className="font-serif not-italic text-[#7A5B14]">déclaré</em>,
              &nbsp;<em className="font-serif not-italic">documenté</em>,
              &nbsp;<em className="font-serif not-italic">vérifié</em>.
            </h2>
            <p className="mt-1 text-xs text-[#5B5B62] leading-relaxed max-w-2xl">
              Plus votre dossier est documenté et cohérent, plus les analyses ALTIORA gagnent en précision.
              Chaque document est cité dans le Bilan PDF avec son niveau de preuve.
            </p>
          </div>
          <button onClick={runBatchAnalyze}
                   disabled={analyzing || documentedPending === 0}
                   data-testid="vault-batch-analyze-btn"
                   className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition
                               ${documentedPending === 0
                                  ? "bg-[#F1EFE8] text-[#B8B5AC] cursor-not-allowed"
                                  : "bg-[#0B2545] text-white hover:bg-[#061630] shadow"}`}>
            {analyzing
              ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
              : <Wand2 className="h-4 w-4" strokeWidth={2} />}
            {analyzing
              ? "Analyse en cours…"
              : documentedPending === 0
                ? "Aucun document à analyser"
                : `Analyser ${documentedPending} document${documentedPending > 1 ? "s" : ""}`}
          </button>
        </header>

        {loading && (
          <div className="px-6 py-8 flex items-center gap-2 text-sm text-[#5B5B62]">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> Chargement…
          </div>
        )}

        {!loading && (
          <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Reliability gauge */}
            <div className="rounded-xl bg-white border border-[#E4E1D8] p-4 flex flex-col items-center justify-center"
                 data-testid="vault-reliability-gauge">
              <ReliabilityDial score={rel} tone={relTone} />
              <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                Fiabilité — {relLabel}
              </div>
            </div>

            {/* KPI stack */}
            <div className="md:col-span-2 grid grid-cols-2 gap-3">
              <KpiTile icon={Layers} label="Pièces jointes" value={totalDocs}
                        hint={documentedPending > 0
                          ? `${documentedPending} en attente d'analyse`
                          : "Toutes analysées"} />
              <KpiTile icon={Sparkles} label="Extraites par IA"
                        value={extractions.length}
                        hint={analyzedPending > 0
                          ? `${analyzedPending} à confirmer`
                          : "—"}
                        accent="#0B2545" />
              <KpiTile icon={FileCheck2} label="Confirmées / vérifiées"
                        value={summary?.verified_count || 0}
                        hint="documents à haut niveau de preuve"
                        accent="#059669" />
              <KpiTile icon={AlertTriangle} label="Incohérences"
                        value={issues.length}
                        hint={issues.length ? "à clarifier" : "aucune détectée"}
                        accent={issues.length ? "#B91C1C" : "#059669"} />
            </div>

            {/* Categories sparkline */}
            <div className="rounded-xl bg-white border border-[#E4E1D8] p-4"
                 data-testid="vault-categories-breakdown">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                Par catégorie
              </div>
              {Object.keys(summary?.by_category || {}).length === 0 ? (
                <div className="mt-3 text-[11px] text-[#8B8B8F]">
                  Aucune pièce jointe pour l'instant.
                </div>
              ) : (
                <ul className="mt-3 space-y-1.5">
                  {Object.entries(summary?.by_category || {})
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 6)
                    .map(([cat, count]) => (
                      <li key={cat} className="flex items-center gap-2 text-[11px]">
                        <span className="font-mono uppercase tracking-widest text-[#5B5B62] w-20 truncate">
                          {CATEGORY_FR[cat] || cat}
                        </span>
                        <div className="flex-1 h-1 bg-[#F1EFE8] rounded-full overflow-hidden">
                          <div className="h-full bg-[#0B2545]"
                               style={{ width: `${Math.min(100, count * 20)}%` }} />
                        </div>
                        <span className="font-mono text-[10px] text-[#0B2545] w-4 text-right">
                          {count}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Inconsistencies list */}
        {issues.length > 0 && (
          <div className="px-6 pb-5" data-testid="vault-inconsistencies-list">
            <div className="rounded-xl border border-red-200 bg-red-50/60 px-4 py-3">
              <div className="flex items-center gap-2 text-xs font-medium text-red-800">
                <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} />
                Incohérences entre données déclarées et documents
              </div>
              <ul className="mt-2 space-y-1">
                {issues.slice(0, 5).map((iss, i) => (
                  <li key={i} className="text-[11px] text-red-900 leading-snug"
                      data-testid={`vault-issue-${i}`}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle
                                       ${iss.severity === "high" ? "bg-red-600" : "bg-amber-500"}`} />
                    {iss.message_fr || iss.field}
                    {iss.source_doc_label && (
                      <span className="text-red-700/70"> — <em>{iss.source_doc_label}</em></span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Extracted findings preview */}
        {extractions.length > 0 && (
          <div className="px-6 pb-6" data-testid="vault-extractions-preview">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
              Données extraites récemment
            </div>
            <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
              {extractions.slice(0, 6).map((e) => (
                <li key={e.id}
                    className="rounded-lg border border-[#EDE7D6] bg-white/80 px-3 py-2"
                    data-testid={`vault-extraction-${e.id}`}>
                  <div className="flex items-center gap-2 text-[11px] text-[#0B2545] truncate">
                    <FileCheck2 className="h-3 w-3 text-[#7A5B14]" strokeWidth={2} />
                    <span className="font-medium truncate flex-1">{e.label}</span>
                    <span className="font-mono text-[9px] text-[#8B8B8F]">
                      {(e.extraction_confidence ?? 0)}% · {CATEGORY_FR[e.category] || e.category}
                    </span>
                  </div>
                  <div className="mt-1 text-[10px] font-mono text-[#5B5B62] line-clamp-2 leading-tight">
                    {Object.entries(e.attributes?.extracted || {})
                      .filter(([k]) => !["raw_text", "confidence"].includes(k))
                      .slice(0, 4)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(" · ")}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}


function KpiTile({ icon: Icon, label, value, hint, accent = "#7A5B14" }) {
  return (
    <div className="rounded-xl bg-white border border-[#E4E1D8] p-3">
      <div className="flex items-center justify-between">
        <div className="text-[9px] font-mono uppercase tracking-widest text-[#5B5B62]">
          {label}
        </div>
        <Icon className="h-3 w-3" style={{ color: accent }} strokeWidth={2} />
      </div>
      <div className="mt-1 font-mono text-2xl tabular-nums leading-none text-[#0B2545]">
        {value}
      </div>
      {hint && (
        <div className="mt-1 text-[9px] font-mono text-[#8B8B8F] leading-tight">
          {hint}
        </div>
      )}
    </div>
  );
}


function ReliabilityDial({ score, tone }) {
  const clamped = Math.max(0, Math.min(100, score));
  const dashArray = 2 * Math.PI * 32;
  const dashOffset = dashArray * (1 - clamped / 100);
  return (
    <svg viewBox="0 0 80 80" className="w-24 h-24">
      <circle cx="40" cy="40" r="32" fill="none" stroke="#F1EFE8" strokeWidth="6" />
      <circle cx="40" cy="40" r="32" fill="none" stroke={tone} strokeWidth="6"
              strokeDasharray={dashArray} strokeDashoffset={dashOffset}
              strokeLinecap="round"
              transform="rotate(-90 40 40)"
              style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      <text x="40" y="46" textAnchor="middle"
            fill={tone} fontSize="20" fontWeight="600"
            fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">
        {clamped}
      </text>
    </svg>
  );
}
